import { currentSlot, formatMass, type ExerciseId, type SessionState, type Unit } from '@repwise/domain';
import { useState } from 'react';
import { wt } from '../lib/copy.ts';
import { LIFT_NAME } from '../lib/defaults.ts';
import { Button } from './ui.tsx';

/** "4 sets building up, then 5 at 72.5 kg" (a ramp to a top set) or "5 × 5 at 82.5 kg". */
export function summary(s: SessionState, exercise: ExerciseId, unit: Unit): string {
  const work = s.slots.filter((x) => x.exercise === exercise && (x.kind === 'work' || x.kind === 'accessory'));
  if (!work.length) return '';
  const top = work.at(-1)!;
  const at = (w: number | null) => (w === null ? '' : ` at ${wt(w, unit)}`);
  const ramp = work.filter((x) => x.role === 'ramp');
  if (ramp.length) return `${ramp.length} sets building up, then ${top.reps} at ${wt(top.weight, unit)}`;
  const same = work.every((x) => x.weight === top.weight && x.reps === top.reps);
  return same ? `${work.length} × ${top.reps}${at(top.weight)}` : work.map((x) => `${x.weight === null ? '' : formatMass(x.weight)}×${x.reps}`).join(', ');
}

/**
 * S12 all-exercises overview (J20): every exercise of the workout with its sets. Rack busy? Do another exercise next;
 * add an extra set to anything. Extra sets are recorded but never count towards progression.
 */
export function AllExercises({ state, onFocus, onExtra, onClose }: { state: SessionState; onFocus: (e: ExerciseId) => void; onExtra: (e: ExerciseId) => void; onClose: () => void }) {
  const unit = state.gym.unit;
  const cur = currentSlot(state);
  const exercises = [...new Set(state.slots.map((x) => x.exercise))];
  const curIdx = cur ? exercises.indexOf(cur.exercise) : -1;
  const [tab, setTab] = useState<'work' | 'warmup'>('work');
  const hasWarmups = state.slots.some((x) => x.kind === 'warmup');
  return (
    <section className="fixed inset-0 z-20 bg-surface overflow-y-auto" role="dialog" aria-modal="true" aria-label="All exercises">
      <div className="max-w-[480px] mx-auto px-5 pt-4" style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 24px)' }}>
        <h1 className="text-title">All exercises</h1>
        <p className="text-sm text-text-muted mt-1">Rack busy? Tap an exercise to do it next.</p>
        {(
          <div role="radiogroup" aria-label="Sets shown" className="flex rounded-full bg-surface-sunken p-1 mt-3 w-fit">
            {(['work', 'warmup'] as const).map((t) => (
              <button key={t} type="button" role="radio" aria-checked={tab === t} className={`min-h-11 px-4 rounded-full text-sm ${tab === t ? 'bg-text text-surface' : ''}`} onClick={() => setTab(t)}>
                {t === 'work' ? 'Workout' : 'Warm-up'}
              </button>
            ))}
          </div>
        )}
        {tab === 'warmup' && !hasWarmups && <p className="text-sm text-text-muted mt-3">No warm-ups today: these weights start at or near the empty bar.</p>}
        <ul className="mt-4 space-y-3">
          {exercises.map((ex, i) => {
            const slots = state.slots.filter((x) => x.exercise === ex && (tab === 'warmup' ? x.kind === 'warmup' : x.kind !== 'warmup'));
            const left = state.slots.filter((x) => x.exercise === ex && !(x.id in state.log));
            const now = cur?.exercise === ex;
            const status = now ? 'In progress' : !left.length ? 'Done' : i === curIdx + 1 || (curIdx < 0 && i === 0) ? 'Up next' : 'Pending';
            const extras = slots.filter((x) => x.kind === 'extra').length;
            return (
              <li key={ex} className={`rounded-[16px] bg-surface-raised p-4 ${now ? 'border-l-4 border-now-ink' : ''}`}>
                <div className="flex items-center justify-between gap-2">
                  <h2 className="text-headline">
                    {LIFT_NAME[ex]} <span className="text-xs text-text-muted align-middle ml-1">{status}</span>
                  </h2>
                  {!now && left.length > 0 && (
                    <Button kind="secondary" onClick={() => onFocus(ex)} label={`Do ${LIFT_NAME[ex]} next`}>
                      Do next
                    </Button>
                  )}
                </div>
                <p className="text-sm text-text-muted">
                  {summary(state, ex, unit)}
                  {extras ? ` · ${extras} extra` : ''}
                </p>
                <ol className="flex flex-wrap gap-2 mt-3" aria-label={`Sets for ${LIFT_NAME[ex]}`}>
                  {slots.map((x) => {
                    const e = state.log[x.id];
                    const done = e && e.reps !== null;
                    const isCur = cur?.id === x.id;
                    return (
                      <li key={x.id} className={`min-w-14 rounded-[12px] px-2 py-1 text-center num text-sm ${done ? 'bg-success text-on-action' : isCur ? 'border-2 border-now-ink' : e ? 'border border-dashed border-outline text-text-muted' : 'bg-surface-sunken text-text-muted'}`}>
                        <span className="block">{x.weight === null ? 'BW' : formatMass(e?.weight ?? x.weight)}</span>
                        <span className="block text-xs">
                          × {e?.reps ?? x.reps}
                          {x.kind === 'extra' ? ' extra' : ''}
                        </span>
                      </li>
                    );
                  })}
                </ol>
                <div className="mt-2">
                  <Button kind="text" onClick={() => onExtra(ex)} label={`Add an extra set of ${LIFT_NAME[ex]}`}>
                    + Extra set
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>
        <p className="text-xs text-text-muted mt-4">Extra sets are recorded but don't count towards progression.</p>
        <div className="mt-4">
          <Button onClick={onClose}>{cur ? `Back to ${LIFT_NAME[cur.exercise]}` : 'Close'}</Button>
        </div>
      </div>
    </section>
  );
}
